<script lang="ts">
  import { onMount } from 'svelte';
  import { initializeEditor, activeTab } from './stores/editor';
  import type { EditorPayload, EditorTab } from './types';
  import LinksTab from './components/LinksTab.svelte';
  import ThemeTab from './components/ThemeTab.svelte';
  import SettingsTab from './components/SettingsTab.svelte';
  import AnalyticsTab from './components/AnalyticsTab.svelte';
  import SubscribersTab from './components/SubscribersTab.svelte';
  import OgImageTab from './components/OgImageTab.svelte';
  import PresentationTab from './components/PresentationTab.svelte';
  import PhonePreview from './components/PhonePreview.svelte';

  type TopTab = 'page' | 'og_image' | 'presentation';

  let {
    profile,
    links = [],
    short_links = [],
    groups = [],
    theme,
    theme_options = {},
    presets = [],
    current_tab = 'links',
    top_tab = 'page',
    stats = { total_views: 0, total_clicks: 0 },
  }: {
    profile: EditorPayload['profile'];
    links: EditorPayload['links'];
    short_links: EditorPayload['short_links'];
    groups: EditorPayload['groups'];
    theme: EditorPayload['theme'];
    theme_options?: EditorPayload['theme_options'];
    presets: EditorPayload['presets'];
    current_tab?: EditorTab;
    top_tab?: TopTab;
    stats: { total_views: number; total_clicks: number };
  } = $props();

  // svelte-ignore state_referenced_locally
  const initialTopTab: TopTab = top_tab ?? 'page';
  let activeTopTab = $state<TopTab>(initialTopTab);

  const topTabs: Array<{ key: TopTab; label: string; description: string }> = [
    { key: 'page', label: '@닉네임 페이지 설정', description: '링크, 디자인, 분석, 구독자' },
    { key: 'og_image', label: 'OG Image', description: '소셜 공유 이미지 템플릿' },
    { key: 'presentation', label: '프레젠테이션', description: '슬라이드 / 발표 자료' },
  ];

  const topTabToPath: Record<TopTab, string> = {
    page: '/my-profile/editor/links',
    og_image: '/my-profile/editor/og-image',
    presentation: '/my-profile/editor/presentation',
  };

  const tabs: Array<{ key: EditorTab; label: string; description: string }> = [
    { key: 'links', label: 'Links', description: '링크와 그룹 구성' },
    { key: 'theme', label: 'Design', description: 'Linktree 스타일 디자인 편집' },
    { key: 'settings', label: 'Settings', description: 'SEO, social, capture' },
    { key: 'analytics', label: 'Analytics', description: '조회와 클릭 요약' },
    { key: 'subscribers', label: 'Subscribers', description: '구독자 목록 및 발송' },
  ];
  const tabToPath: Record<EditorTab, string> = {
    links: '/my-profile/editor/links',
    theme: '/my-profile/editor/design',
    settings: '/my-profile/editor/settings',
    analytics: '/my-profile/editor/analytics',
    subscribers: '/my-profile/editor/subscribers',
  };
  const pathToTab: Record<string, EditorTab> = {
    links: 'links',
    design: 'theme',
    settings: 'settings',
    analytics: 'analytics',
    subscribers: 'subscribers',
  };

  const pathToTopTab: Record<string, TopTab> = {
    'og-image': 'og_image',
    presentation: 'presentation',
  };

  function selectTopTab(key: TopTab) {
    activeTopTab = key;
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.pathname = topTabToPath[key];
    url.searchParams.delete('tab');
    window.history.pushState({ topTab: key }, '', url);
    if (key === 'page') {
      activeTab.set('links');
    }
  }

  $effect(() => {
    initializeEditor({ profile, links, short_links, groups, theme, theme_options, presets, stats });
  });

  $effect(() => {
    if (tabs.some((tab) => tab.key === current_tab)) {
      activeTab.set(current_tab);
    }
  });

  function syncTabUrl(tab: EditorTab, mode: 'push' | 'replace' = 'push') {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.pathname = tabToPath[tab];
    url.searchParams.delete('tab');
    if (mode === 'replace') {
      window.history.replaceState({ tab }, '', url);
    } else {
      window.history.pushState({ tab }, '', url);
    }
  }

  function selectTab(tab: EditorTab) {
    activeTab.set(tab);
    syncTabUrl(tab, 'push');
  }

  onMount(() => {
    if (typeof window === 'undefined') return undefined;

    const initialSegment = window.location.pathname.split('/').pop() || 'links';
    const initialTopTab = pathToTopTab[initialSegment];
    if (initialTopTab) {
      activeTopTab = initialTopTab;
    }
    const queryTab = new URLSearchParams(window.location.search).get('tab');
    const initialTab = pathToTab[initialSegment] || (queryTab && tabs.some((tab) => tab.key === queryTab) ? (queryTab as EditorTab) : null);
    if (initialTab) {
      activeTab.set(initialTab);
    } else if (!initialTopTab) {
      syncTabUrl('links', 'replace');
    }

    const onPopstate = () => {
      const nextSegment = window.location.pathname.split('/').pop() || 'links';
      const nextTopTab = pathToTopTab[nextSegment];
      if (nextTopTab) {
        activeTopTab = nextTopTab;
        return;
      }
      activeTopTab = 'page';
      const nextTab = pathToTab[nextSegment];
      if (nextTab && tabs.some((tab) => tab.key === nextTab)) {
        activeTab.set(nextTab);
      }
    };

    window.addEventListener('popstate', onPopstate);
    return () => window.removeEventListener('popstate', onPopstate);
  });
</script>

  <div class="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
    <div class="rounded-[2rem] border border-gray-200 bg-[linear-gradient(135deg,#fff_0%,#f8fbff_52%,#eef6ff_100%)] p-6 shadow-sm">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-600">Profile Editor</p>
      <div class="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="text-3xl font-semibold tracking-tight text-gray-950">{profile.display_name}</h1>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            실제 공개 프로필 데이터를 기준으로 편집하는 split workspace입니다. 우측 preview는 공개 페이지를 방문자 시점 그대로 띄우고 디자인 변경은 저장 전에도 바로 보여줍니다.
          </p>
        </div>
        <a
          href={profile.username ? `/@${profile.username}` : '/profile'}
          target="_blank"
          rel="noreferrer"
          class="inline-flex items-center justify-center rounded-full bg-gray-950 px-5 py-3 text-sm font-medium text-white transition hover:bg-gray-800"
        >
          Public page 보기
        </a>
      </div>
    </div>

    <div class="flex gap-2 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-sm">
      {#each topTabs as tt}
        <button
          type="button"
          class={`flex-1 rounded-xl px-4 py-3 text-left transition ${
            activeTopTab === tt.key
              ? 'bg-gray-950 text-white shadow'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          onclick={() => selectTopTab(tt.key)}
        >
          <div class="text-sm font-semibold">{tt.label}</div>
          <div class={`mt-0.5 text-[11px] leading-4 ${activeTopTab === tt.key ? 'text-white/70' : 'text-gray-500'}`}>
            {tt.description}
          </div>
        </button>
      {/each}
    </div>

    {#if activeTopTab === 'page'}
      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_360px]">
        <div class="space-y-6">
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {#each tabs as tab}
              <button
                type="button"
                class={`rounded-2xl border px-4 py-4 text-left transition ${
                  $activeTab === tab.key
                    ? 'border-gray-950 bg-gray-950 text-white shadow-lg shadow-gray-950/10'
                    : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onclick={() => selectTab(tab.key)}
              >
                <div class="text-sm font-semibold">{tab.label}</div>
                <div class={`mt-1 text-xs leading-5 ${$activeTab === tab.key ? 'text-white/70' : 'text-gray-500'}`}>
                  {tab.description}
                </div>
              </button>
            {/each}
          </div>

          {#if $activeTab === 'links'}
            <LinksTab />
          {:else if $activeTab === 'theme'}
            <ThemeTab />
          {:else if $activeTab === 'settings'}
            <SettingsTab />
          {:else if $activeTab === 'subscribers'}
            <SubscribersTab />
          {:else}
            <AnalyticsTab />
          {/if}
        </div>

        <div>
          <PhonePreview />
        </div>
      </div>
    {:else if activeTopTab === 'og_image'}
      <OgImageTab />
    {:else if activeTopTab === 'presentation'}
      <PresentationTab />
    {/if}
  </div>
